<script setup lang="ts">
import { computed } from 'vue';
import { DgrIcon } from '@stockmarkteam/donguri-ui';
import { insightAxisPresentations } from '@/components/insights/insight-display';
import type { InsightAxisType } from '@/types/insights';

interface Props {
  requiresProfile?: boolean;
}
const props = defineProps<Props>();

const sentenceTail = computed(
  () =>
    `から、アイデアを考えるための着眼点とその仮説をお届けします${
      props.requiresProfile ? '（業務情報が必要です）' : ''
    }。`,
);

const axisOrder: InsightAxisType[] = ['personal', 'organization', 'outside'];
const axes = axisOrder.map(type => ({
  type,
  ...insightAxisPresentations[type],
}));
</script>

<template>
  <span class="insight-topics-description">
    <VTooltip
      theme="popover"
      placement="bottom-start"
      popper-class="insight-topics-popper"
      class="trigger-container"
    >
      <span class="trigger">３種類のトピック</span>
      <template #popper>
        <div class="topics-popover">
          <div class="c-title-medium--m title">
            <DgrIcon
              name="summary"
              size="small"
              :keep-fill="false"
              class="icon"
            />
            トピック
          </div>
          <div class="c-text-regular--m body">
            トピックとは、発想のヒントのもとになるニュースを、要点だけにまとめたものです。トピックは、次の3種類が存在します
          </div>
          <ul class="axes">
            <li v-for="axis in axes" :key="axis.type" class="axis">
              <div class="c-title-medium--m axis-title">
                <DgrIcon
                  :name="axis.icon"
                  size="small"
                  :keep-fill="false"
                  class="icon"
                />
                {{ axis.label }}
              </div>
              <div class="c-text-regular--m axis-description">
                {{ axis.description }}
              </div>
            </li>
          </ul>
        </div>
      </template>
    </VTooltip>
    <span>{{ sentenceTail }}</span>
  </span>
</template>

<style lang="scss" scoped>
.v-popper.trigger-container {
  display: inline;

  .trigger {
    cursor: default;
    text-decoration: underline;
  }
}

.topics-popover {
  display: flex;
  flex-direction: column;
  gap: 8px;
  color: $color-gray1000;

  // DgrIcon のサイズ指定に 20px がないので、デザインの値に上書きする。
  .icon {
    width: 20px;
    height: 20px;
  }

  .title {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  .axes {
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin: 8px 0 0;
    padding: 16px;
    list-style: none;
    border: 1px solid $color-gray400;
    border-radius: 4px;

    .axis {
      display: flex;
      flex-direction: column;
      gap: 8px;

      & + .axis {
        padding-top: 16px;
        border-top: 1px solid $color-gray400;
      }
    }

    .axis-title {
      display: flex;
      align-items: center;
      gap: 4px;
    }
  }
}
</style>
